import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { CheckCircle2, CloudUpload } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadInProgressNoticeProps {
  /** `uploading` while bytes are still in flight, `complete` once the batch is sealed. */
  phase: 'uploading' | 'complete';
  /** Overall upload progress, 0..100. Only shown while uploading. */
  progress?: number;
  className?: string;
}

/**
 * Inline callout shown next to the upload progress on the Data Center page.
 *
 * While uploading it asks the user to stay on the page (closing the tab or
 * navigating away aborts the multipart upload) and explains that processing
 * only unlocks once the upload has finished. Once the batch is sealed it flips
 * to a success state so no stale warning is left behind.
 */
export const UploadInProgressNotice: FunctionComponent<UploadInProgressNoticeProps> = ({
  phase,
  progress,
  className,
}) => {
  const { t } = useTranslation('project');
  const uploading = phase === 'uploading';
  const pct = Math.max(0, Math.min(100, Math.round(progress ?? 0)));

  return (
    <Alert
      role='status'
      aria-live='polite'
      data-testid='upload-in-progress-notice'
      data-phase={phase}
      className={cn(
        uploading
          ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400'
          : 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100 [&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400',
        className,
      )}>
      {uploading ? <CloudUpload className='h-4 w-4 animate-pulse' /> : <CheckCircle2 className='h-4 w-4' />}
      <AlertTitle className='font-semibold'>
        {uploading ? t('upload.stayOnPageTitle') : t('upload.uploadCompleteTitle')}
      </AlertTitle>
      <AlertDescription className={uploading ? 'text-amber-800 dark:text-amber-200' : 'text-emerald-800 dark:text-emerald-200'}>
        {uploading ? t('upload.stayOnPageDescription') : t('upload.uploadCompleteDescription')}
        {uploading && (
          <div className='mt-3 flex items-center gap-3'>
            <div
              className='relative h-1.5 flex-1 overflow-hidden rounded-full bg-amber-200/70 dark:bg-amber-900/60'
              role='progressbar'
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}>
              <div
                className='h-full bg-amber-500 transition-[width] duration-300 dark:bg-amber-400'
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className='text-xs font-semibold tabular-nums' data-testid='upload-in-progress-percent'>
              {pct}%
            </span>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
};

export default UploadInProgressNotice;
